import { useAppDispatch } from 'app/hooks';
import { useForm } from 'hooks/useForm';
import { FormEvent, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';
import { TERMINAL_EDIT_FIELDS_SCHEMA, TERMINAL_INITIAL_FIELDS } from '../constants/terminal-fields';
import { useEditTerminalMutation, useGetTerminalQuery } from '../services';
import { Terminal, TerminalDto } from '../types/dtos';

const useController = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editTerminal, { isLoading: isEditTerminalLoading }] = useEditTerminalMutation();
    const { data: terminal } = useGetTerminalQuery(id as string);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } =
        useForm<Terminal>({
            validationSchema: TERMINAL_EDIT_FIELDS_SCHEMA,
            options: { trackChangedValues: true },
        });

    useEffect(() => {
        const editTerminal = {
            ...terminal,
            email: terminal?.account?.email ?? '',
        };
        if (editTerminal) setInitialFormData({ ...TERMINAL_INITIAL_FIELDS, ...editTerminal });
    }, [setInitialFormData, terminal]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'terminal.TERMINAL_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        let data = { ...changedValues };
        if (data.email && data.password) {
            data = { ...data, account: { email: data.email, password: data.password } } as TerminalDto;
            delete data.email;
            delete data.password;
        } else if (data.email) {
            data = { ...data, account: { email: data.email } } as TerminalDto;
            delete data.email;
        } else if (data.password) {
            data = { ...data, account: { password: data.password } } as TerminalDto;
            delete data.password;
        }

        return trimValues(data) as TerminalDto;
    }, [changedValues]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await editTerminal({
                        id: id as string,
                        payload,
                    }).unwrap();
                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [editTerminal, getPayload, handleErrors, handleSubmit, handleSuccess, id, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as Terminal,
        onChange: handleChange,
        onSubmit,
        onCancel,
        isLoading: formData === undefined,
        isEditTerminalLoading,
        errors,
        dataChanged: isDirty,
    };
};

export default useController;
